import { Injectable } from '@angular/core';
import { UserReview } from 'shared/src/components/user-review/user-review.component';
import { FunnelSampleContentService } from 'shared/src/services/funnel-sample-content.service';
import {
  BookId,
  fictionInterestBookIdsMap,
  nonFictionInterestBookIdsMap,
  readerGoalBookIdsMap,
} from '../../../../funnel/src/app/data/books';
import {
  FictionInterestId,
  highlightText,
  NonFictionInterestId,
  ReaderGoalId,
} from '../../../../funnel/src/app/data/quiz';
import { FunnelFlowService } from '../../../../funnel/src/app/services/funnel-flow.service';
import {
  PaywallAudioTrackSection,
  PaywallFeature,
  PaywallService,
} from '../../../../funnel/src/app/services/paywall.service';

declare const $localize: any;

const defaultReaderFictionBookIds: readonly BookId[] = [
  'projectHailMary',
  'theSevenHusbandsOfEvelynHugo',
];
const defaultReaderNonFictionBookIds: readonly BookId[] = ['atomicHabits', 'thePsychologyOfMoney'];
const maxReaderPaywallSamples = 8;
const maxReaderPaywallFictionSamples = 4;

@Injectable({
  providedIn: 'root',
})
export class ReaderPaywallService extends PaywallService {
  constructor(
    protected override flowService: FunnelFlowService,
    private sampleContentService: FunnelSampleContentService
  ) {
    super(flowService);
  }

  override getTitle(): string {
    return (
      $localize`:@@readerPaywallTitlePrefix:Your ` +
      highlightText($localize`:@@readerPaywallTitleHighlight:audio reading plan`) +
      $localize`:@@readerPaywallTitleSuffix: is ready`
    );
  }

  override getContextualTextBundle(): { title: string; beforeText: string; afterText: string } {
    return {
      title: $localize`:@@kindleContextTitle:Reading Flow`,
      beforeText: $localize`:@@kindleContextBefore:Books waiting unread`,
      afterText: $localize`:@@kindleContextAfter:Chapters finished in spare moments`,
    };
  }

  override getReviews(): UserReview[] {
    return [
      {
        name: $localize`:@@kindleReviewName1:Laura Bennett`,
        age: 41,
        message: $localize`:@@kindleReviewMessage1:I had so many books sitting half-finished because evenings were the only time I could read. Now I listen while walking and cooking, and I finally feel connected to my reading list again.`,
        avatarUrl: 'assets/img/avatars/w3.jpg',
        rate: 4.9,
      },
      {
        name: $localize`:@@kindleReviewName2:Marcus Reed`,
        age: 36,
        message: $localize`:@@kindleReviewMessage2:The biggest surprise was how natural long chapters feel as audio. I use Peech for books I already planned to read, and it turns small gaps in the day into real progress.`,
        avatarUrl: 'assets/img/avatars/m2.jpg',
        rate: 4.8,
      },
    ];
  }

  override getPlanCta(): string {
    return $localize`:@@kindlePaywallPlanCta:Start your plan while your reader discount is active`;
  }

  override getHighlights(): string[] {
    return [
      $localize`:@@kindlePaywallHighlight1:Turn long chapters into natural audio`,
      $localize`:@@kindlePaywallHighlight2:Listen during walks, chores, commutes, and screen-free breaks`,
      $localize`:@@kindlePaywallHighlight3:Voices tuned for long-form books, not robotic snippets`,
      $localize`:@@kindlePaywallHighlight4:Speed control for finishing more chapters`,
      $localize`:@@kindlePaywallHighlight5:A reading routine built around books you already meant to finish`,
    ];
  }

  override getFeatures(): PaywallFeature[] {
    return [
      {
        emoji: '📖',
        title: $localize`:@@kindleFeature1Title:Turn book text into audio`,
        description: $localize`:@@kindleFeature1Desc:Use Peech with supported ebooks, documents, links, and readable text you provide.`,
      },
      {
        emoji: '🎧',
        title: $localize`:@@kindleFeature2Title:Natural voices for long chapters`,
        description: $localize`:@@kindleFeature2Desc:Clear narration that stays comfortable through fiction, non-fiction, and dense passages.`,
      },
      {
        emoji: '📚',
        title: $localize`:@@kindleFeatureLibraryTitle:Convert your Kindle library into audiobooks`,
        description: $localize`:@@kindleFeatureLibraryDesc:Turn your books into natural-sounding audio in just a few taps.`,
      },
      {
        emoji: '🚶',
        title: $localize`:@@kindleFeature3Title:Read while your hands are busy`,
        description: $localize`:@@kindleFeature3Desc:Keep books moving during walks, chores, workouts, errands, and commutes.`,
      },
      {
        emoji: '👀',
        title: $localize`:@@kindleFeature4Title:Give your eyes a break`,
        description: $localize`:@@kindleFeature4Desc:Switch from screen time to listening when reading at night feels tiring.`,
      },
      {
        emoji: '⏩',
        title: $localize`:@@kindleFeature5Title:Control the pace`,
        description: $localize`:@@kindleFeature5Desc:Slow down for complex chapters or speed up familiar sections without losing the thread.`,
      },
      {
        emoji: '✅',
        title: $localize`:@@kindleFeature6Title:Built for finishing`,
        description: $localize`:@@kindleFeature6Desc:Turn small pockets of time into real progress on books waiting in your library.`,
      },
    ];
  }

  override getAudioTrackSections(): PaywallAudioTrackSection[] {
    const fictionInterestIds = this.flowService.getFictionInterestIds();
    const nonFictionInterestIds = this.flowService.getNonFictionInterestIds();
    const readerGoalIds = this.flowService.getReaderGoalIds();
    const prioritizedFictionBookIds = this.getBookIdsForInterests(
      fictionInterestIds,
      fictionInterestBookIdsMap,
      defaultReaderFictionBookIds
    );
    const fictionBookIds = this.takeUniqueBookIds(
      prioritizedFictionBookIds,
      maxReaderPaywallFictionSamples
    );
    const readerGoalBookIds = this.getReaderGoalBookIds(readerGoalIds);
    const readerGoalReserve = Math.min(
      readerGoalBookIds.length,
      this.getRemainingSampleLimit(fictionBookIds.length) > 0 ? 1 : 0
    );
    const nonFictionCandidateBookIds = this.getBookIdsForInterests(
      nonFictionInterestIds,
      nonFictionInterestBookIdsMap,
      defaultReaderNonFictionBookIds
    );
    const nonFictionInterestBookIds = this.takeUniqueBookIds(
      nonFictionCandidateBookIds,
      this.getRemainingSampleLimit(fictionBookIds.length) - readerGoalReserve,
      fictionBookIds
    );
    const readerGoalSampleBookIds = this.takeUniqueBookIds(
      readerGoalBookIds,
      this.getRemainingSampleLimit(fictionBookIds.length + nonFictionInterestBookIds.length),
      [...fictionBookIds, ...nonFictionInterestBookIds]
    );
    const nonFictionBookIds = [...nonFictionInterestBookIds, ...readerGoalSampleBookIds];
    const extraFictionBookIds = this.takeUniqueBookIds(
      prioritizedFictionBookIds,
      this.getRemainingSampleLimit(fictionBookIds.length + nonFictionBookIds.length),
      [...fictionBookIds, ...nonFictionBookIds]
    );
    const displayedFictionBookIds = [...fictionBookIds, ...extraFictionBookIds];

    return [
      {
        title:
          fictionInterestIds.length > 0
            ? $localize`:@@kindleFictionSamplesSelected:🎭 Fiction picks from your shelf`
            : $localize`:@@kindleFictionSamplesDefault:🎭 Popular fiction samples`,
        tracks: this.sampleContentService.getBooks(displayedFictionBookIds),
      },
      {
        title:
          nonFictionInterestIds.length > 0 || readerGoalIds.length > 0
            ? $localize`:@@readerNonFictionSamplesSelected:📚 Picks for your goals`
            : $localize`:@@readerNonFictionSamplesDefault:📚 Popular non-fiction samples`,
        tracks: this.sampleContentService.getBooks(nonFictionBookIds),
      },
    ].filter(section => section.tracks.length > 0);
  }

  private getBookIdsForInterests<T extends FictionInterestId | NonFictionInterestId>(
    interestIds: T[],
    map: Record<T, readonly BookId[]>,
    fallbackIds: readonly BookId[]
  ): BookId[] {
    const mappedBookIds = this.getDiverseBookIdsForInterests(interestIds, map);

    return mappedBookIds.length > 0
      ? mappedBookIds
      : this.takeUniqueBookIds(fallbackIds, fallbackIds.length);
  }

  private getDiverseBookIdsForInterests<T extends FictionInterestId | NonFictionInterestId>(
    interestIds: T[],
    map: Record<T, readonly BookId[]>
  ): BookId[] {
    const result: BookId[] = [];
    const groups = interestIds.map(interestId => map[interestId] || []);
    const maxGroupLength = Math.max(0, ...groups.map(group => group.length));

    for (let index = 0; index < maxGroupLength; index++) {
      groups.forEach(group => this.addBookId(result, group[index]));
    }

    return result;
  }

  private getReaderGoalBookIds(readerGoalIds: ReaderGoalId[]): BookId[] {
    const result: BookId[] = [];

    readerGoalIds.forEach(readerGoalId => {
      readerGoalBookIdsMap[readerGoalId].forEach(bookId => this.addBookId(result, bookId));
    });

    return result;
  }

  private takeUniqueBookIds(
    bookIds: readonly BookId[],
    limit: number,
    excludedBookIds: readonly BookId[] = []
  ): BookId[] {
    const result: BookId[] = [];
    const excluded = new Set(excludedBookIds);

    for (const bookId of bookIds) {
      if (result.length >= limit) {
        break;
      }

      if (!excluded.has(bookId) && !result.includes(bookId)) {
        result.push(bookId);
      }
    }

    return result;
  }

  private addBookId(target: BookId[], bookId: BookId | undefined): void {
    if (bookId && !target.includes(bookId)) {
      target.push(bookId);
    }
  }

  private getRemainingSampleLimit(selectedCount: number): number {
    return Math.max(0, maxReaderPaywallSamples - selectedCount);
  }

  override getSocialProofText(totalUsersThisHour: number): string {
    return $localize`:@@kindleReadersStartedLastHour:${totalUsersThisHour}:INTERPOLATION: readers started a listening plan in the last hour`;
  }

  override getCommunityTitle(): string {
    return (
      $localize`:@@kindleCommunityTitlePrefix:Join readers ` +
      highlightText($localize`:@@kindleCommunityTitleHighlight:finishing more books`) +
      $localize`:@@kindleCommunityTitleSuffix: with Peech`
    );
  }
}
